// src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { useAiContext } from '../hooks/useAiContext';

import ProgressWidget from '../components/ProgressWidget';
import PapChatLoader from '../components/PapChatLoader';
import AiGesprekkenLijst from '../components/AiGesprekkenLijst';
import DashboardSuggesties from '../components/DashboardSuggesties';
import { usePapConversation } from '../hooks/usePapConversation';

import { ReactComponent as NewsIcon } from '../assets/icon-news.svg';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

const SkeletonDashboard = () => (
  <main className="dashboard-page-content grid-layout skeleton-loading">
    <section className="main-panel ai-assistant-panel">
      <div className="overview-header">
        <div className="skeleton-line" style={{ width: '40%', height: '2.5rem', margin: '0' }}></div>
      </div>
      <div style={{ height: '500px', backgroundColor: '#f9fafb', borderRadius: '16px' }}></div>
    </section>
    <aside className="side-panel">
      <div className="widget-card" style={{ padding: '1.5rem' }}>
        <div className="skeleton-line" style={{ width: '100%', height: '100px' }}></div>
      </div>
    </aside>
  </main>
);

function Dashboard() {
  const [featuredItems, setFeaturedItems] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Carousel state — UITGELICHT
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [slideDirectionFeatured, setSlideDirectionFeatured] = useState('next');

  // Carousel state — Nieuws
  const [newsIndex, setNewsIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('next');

  const { doorloopjes, currentUser } = useDoorloopjes();
  
  const getContext = useAiContext();

  // Koppeling tussen de chatwidget en het account (actief gesprek, gesprekkenlijst)
  const { getConversationId, onConversationIdUpdate, onConversationReset } = usePapConversation();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    const headers = { Authorization: `Bearer ${jwt}` };

    const fetchFeaturedArticles = axios.get(
      `${STRAPI_URL}/api/artikels?filters[is_featured][$eq]=true&sort=publication_date:desc&populate=featured_image`,
      { headers }
    );
    const fetchFeaturedDigidates = axios.get(
      `${STRAPI_URL}/api/digidates?filters[is_featured][$eq]=true&sort=event_date:desc&populate=image`,
      { headers }
    );
    const fetchNews = axios.get(
      `${STRAPI_URL}/api/nieuws-items?sort=publication_date:desc`,
      { headers }
    );

    Promise.all([fetchFeaturedArticles, fetchFeaturedDigidates, fetchNews])
      .then(([articlesRes, digidatesRes, newsRes]) => {
        const featured = [
          ...articlesRes.data.data.map(x => ({
            ...x,
            __type: 'artikel',
            __date: x.publication_date,
          })),
          ...digidatesRes.data.data.map(x => ({
            ...x,
            __type: 'digidates',
            __date: x.event_date,
          })),
        ].sort((a, b) => {
          if (a.__type === 'digidates' && b.__type === 'digidates') {
            const aUpcoming = !a.has_taken_place;
            const bUpcoming = !b.has_taken_place;
            if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
            if (aUpcoming) return new Date(a.__date) - new Date(b.__date);
            return new Date(b.__date) - new Date(a.__date);
          }
          return new Date(b.__date) - new Date(a.__date);
        });

        setFeaturedItems(featured);

        const newsOnly = newsRes.data.data
          .map(item => ({ ...item, __type: 'nieuws-item' }))
          .sort((a, b) => new Date(b.publication_date) - new Date(a.publication_date));

        setLatestNews(newsOnly);
      })
      .catch(error => console.error("Fout bij ophalen dashboard data:", error))
      .finally(() => setLoading(false));
  }, []);

  const handlePrevFeatured = () => { if (featuredItems.length > 1) { setSlideDirectionFeatured('prev'); setFeaturedIndex(featuredIndex === 0 ? featuredItems.length - 1 : featuredIndex - 1); } };
  const handleNextFeatured = () => { if (featuredItems.length > 1) { setSlideDirectionFeatured('next'); setFeaturedIndex((featuredIndex + 1) % featuredItems.length); } };
  const handlePrevNews = () => { if (latestNews.length > 1) { setSlideDirection('prev'); setNewsIndex(newsIndex === 0 ? latestNews.length - 1 : newsIndex - 1); } };
  const handleNextNews = () => { if (latestNews.length > 1) { setSlideDirection('next'); setNewsIndex((newsIndex + 1) % latestNews.length); } };

  const SpotlightCard = ({ item, animateDir }) => {
    let iconUrl = null;
    let to = '#';
    const title = item.title;
    const summary = item.summary || '';

    if (item.__type === 'artikel') {
      iconUrl = item.featured_image?.formats?.thumbnail?.url || item.featured_image?.url;
      to = `/artikelen/${item.slug}`;
    } else if (item.__type === 'digidates') {
      iconUrl = item.image?.formats?.thumbnail?.url || item.image?.url;
      to = `/webinars/${item.slug}`;
    }

    return (
      <Link to={to} className={`widget-item-card animated-card ${animateDir}`}>
        {iconUrl ? (
          <img src={iconUrl} alt={title} className="widget-item-icon" loading="lazy" />
        ) : (
          <div className="widget-item-icon placeholder"></div>
        )}
        <div className="widget-item-text">
          <h4>{title}</h4>
          {item.__type === 'digidates' && item.event_date && (
            <p style={{ fontWeight: 700, color: '#58595b', margin: 0 }}>
              {new Date(item.event_date).toLocaleDateString('nl-NL', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          )}
          {summary ? <p>{summary}</p> : null}
        </div>
      </Link>
    );
  };

  if (loading || !currentUser) return <SkeletonDashboard />;

  const heeftVoortgang =
    (currentUser.inprogress_doorloopjes?.length || 0) + (currentUser.completed_doorloopjes?.length || 0) > 0;
  const currentNewsItem = latestNews[newsIndex];
  const currentFeaturedItem = featuredItems[featuredIndex];

  return (
    <main className="dashboard-page-content grid-layout">
      {/* LEFT PANEL: Modern AI Assistant (desktop). Op mobiel opent de chat schermvullend
          via het ondermenu; een lege container van 600px lag daar over de kaarten heen. */}
      <section className="main-panel">
        {!isMobile && (
          <div className="ai-assistant-container-modern" style={{ height: '100%', minHeight: '600px', position: 'relative' }}>
            <div className="ai-assistant-met-gesprekken">
              <AiGesprekkenLijst />
              <div id="pap-chat-embedded" className="ai-assistant-chat"></div>
              <PapChatLoader 
                mode="embedded" 
                elementId="pap-chat-embedded" 
                assistantId="doorloopjes" 
                context={getContext}
                storageMode="integrated"
                getConversationId={getConversationId}
                onConversationIdUpdate={onConversationIdUpdate}
                onConversationReset={onConversationReset}
              />
            </div>
          </div>
        )}
      </section>

      <aside className="side-panel">
        {isMobile && <AiGesprekkenLijst variant="kaart" />}
        {/* Op desktop de bovenste kaart, op mobiel onder de gesprekkenkaart. */}
        <DashboardSuggesties />
        <div className="widget-card">
          <div className="widget-header">
            <h3>Uitgelicht</h3>
            <div className="widget-nav">
              <span onClick={handlePrevFeatured} style={{ cursor: featuredItems.length > 1 ? 'pointer' : 'default', opacity: featuredItems.length > 1 ? 1 : 0.4 }}>{'<'}</span>
              <span onClick={handleNextFeatured} style={{ cursor: featuredItems.length > 1 ? 'pointer' : 'default', opacity: featuredItems.length > 1 ? 1 : 0.4 }}>{'>'}</span>
            </div>
          </div>
          {currentFeaturedItem ? (
            <SpotlightCard item={currentFeaturedItem} animateDir={slideDirectionFeatured} />
          ) : (
            <p>Geen uitgelichte items.</p>
          )}
        </div>

        {/* Zonder voortgang staat de cirkel alleen op nul; dan liever niets tonen. */}
        {heeftVoortgang && (
          <ProgressWidget
            total={doorloopjes.length}
            completed={currentUser.completed_doorloopjes?.length || 0}
            inProgress={currentUser.inprogress_doorloopjes?.length || 0}
          />
        )}

        {currentNewsItem && (
          <div className="widget-card">
            <div className="widget-header">
              <h3>Nieuws</h3>
              <div className="widget-nav">
                <span onClick={handlePrevNews} style={{ cursor: 'pointer' }}>{'<'}</span>
                <span onClick={handleNextNews} style={{ cursor: 'pointer' }}>{'>'}</span>
              </div>
            </div>
            <Link to={`/nieuws-item/${currentNewsItem.slug}`} className={`widget-item-card animated-card ${slideDirection}`}>
              <div className="widget-item-icon"><NewsIcon /></div>
              <div className="widget-item-text">
                <h4>{currentNewsItem.title}</h4>
                <p>{currentNewsItem.summary}</p>
              </div>
            </Link>
          </div>
        )}
      </aside>
    </main>
  );
}

export default Dashboard;